import React, { useEffect, useRef, useState } from 'react'
import { useSplineViewer, SplineViewerProvider } from '@lib/hooks/useSplineViewer'
import { useMediaQuery, useWebGLSupport } from '@lib/hooks'
import { breakpoints } from '@grammarly/badger-ui'
import { SplineViewer } from '../../ui-lib/spline-viewer'
import { useFirstEnterViewport } from '@components/ui-lib/hooks/viewport-tracker'
import { withPublicAssetUrl } from '@lib/utils/cdn'

// Single Spline scene (footer)
const BTS_AGENTS_25_FOOTER_SCENE = {
  id: 'bts-agents-footer',
  title: 'BTS Agents Footer',
  url: 'https://prod.spline.design/GkKAwJ2YGZVoVAv2/scene.splinecode',
  width: 600,
  height: 600,
  fallbackImage: 'bts-hero/keychain-static.png',
}

// PNG fallback image (also used when the viewer is not ready yet)
const FallbackImage: React.FC = () => (
  <img
    src={withPublicAssetUrl(BTS_AGENTS_25_FOOTER_SCENE.fallbackImage)}
    alt={BTS_AGENTS_25_FOOTER_SCENE.title}
    style={{
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      zIndex: 5,
    }}
  />
)

const BtsKeychainInner: React.FC = () => {
  const { registerViewer } = useSplineViewer()
  const isWebGLSupported = useWebGLSupport()
  const isScreenWidthSmall = useMediaQuery(`(max-width: ${breakpoints.small})`)

  useEffect(() => {
    registerViewer(BTS_AGENTS_25_FOOTER_SCENE.id)
  }, [registerViewer])

  // Fallback to static image when WebGL is not supported
  if (!isWebGLSupported) {
    return (
      <div
        style={{
          width: '100%',
          height: `${BTS_AGENTS_25_FOOTER_SCENE.height}px`,
          position: 'relative',
        }}
      >
        <FallbackImage />
      </div>
    )
  }

  return (
    <div
      style={{
        width: '100%',
        height: `${BTS_AGENTS_25_FOOTER_SCENE.height}px`,
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: `${BTS_AGENTS_25_FOOTER_SCENE.width}px`,
          height: `${BTS_AGENTS_25_FOOTER_SCENE.height}px`,
          maxWidth: '100%',
          maxHeight: '100%',
        }}
      >
        <SplineViewer
          url={BTS_AGENTS_25_FOOTER_SCENE.url}
          viewerId={BTS_AGENTS_25_FOOTER_SCENE.id}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            left: isScreenWidthSmall ? -87 : 0,
            transform: isScreenWidthSmall ? 'scale(0.8)' : 'scale(1)',
            zIndex: 10,
            pointerEvents: 'auto',
          }}
        />
      </div>

      <noscript>
        <div
          style={{
            width: '100%',
            height: `${BTS_AGENTS_25_FOOTER_SCENE.height}px`,
            position: 'relative',
          }}
        >
          <FallbackImage />
        </div>
      </noscript>
    </div>
  )
}

export const BtsKeychainComponent: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [shouldRender, setShouldRender] = useState(false)

  useFirstEnterViewport(
    containerRef,
    () => {
      setTimeout(() => setShouldRender(true), 1000) // 1 second delay after entering viewport
    },
    0.5 // Start observing when 50% visible instead of 5%
  )

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: `${BTS_AGENTS_25_FOOTER_SCENE.height}px`,
        position: 'relative',
      }}
    >
      {shouldRender && (
        <SplineViewerProvider>
          <BtsKeychainInner />
        </SplineViewerProvider>
      )}

      <noscript>
        <div
          style={{
            position: 'absolute',
            inset: 0,
          }}
        >
          <FallbackImage />
        </div>
      </noscript>
    </div>
  )
}
